/**
 * ConfirmDialog -- Modal confirming clock-in selections before submission.
 * SDD 7.3 -- Shows category, position, apparatus before finalizing.
 */

import { CATEGORIES, getSubcategoriesForCategory } from '@shared/categories';
import type { CategoryId } from '@shared/categories';
import { getPositionLabel } from '@shared/positions';
import type { PositionId } from '@shared/positions';

interface ConfirmDialogProps {
  category: CategoryId;
  subcategory?: string | null;
  position: string | null;
  apparatus: string | null;
  submitting: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  shiftTimeLabel?: string | null;
  submitLabel?: string;
}

function getCategoryLabel(id: CategoryId): string {
  return CATEGORIES.find((c) => c.id === id)?.label ?? id;
}

function getSubcategoryLabel(category: CategoryId, subcategoryId: string): string {
  const subs = getSubcategoriesForCategory(category);
  return subs?.find((s) => s.id === subcategoryId)?.label ?? subcategoryId;
}

export default function ConfirmDialog({
  category,
  subcategory,
  position,
  apparatus,
  submitting,
  onConfirm,
  onCancel,
  shiftTimeLabel,
  submitLabel,
}: ConfirmDialogProps) {
  const isShiftSubmit = !!shiftTimeLabel;
  const title = isShiftSubmit ? 'Confirm Shift Submission' : 'Confirm Clock In';
  const btnText = submitLabel ?? (isShiftSubmit ? 'Submit' : 'Clock In');
  const btnLoadingText = isShiftSubmit ? 'Submitting...' : 'Clocking In...';

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      className="fixed inset-0 flex items-center justify-center bg-black/70 z-[1000] p-6 backdrop-blur-sm"
      onClick={(e) => {
        if (e.target === e.currentTarget && !submitting) onCancel();
      }}
    >
      <div className="w-full max-w-[360px] bg-surface-overlay rounded-2xl border border-edge-default p-6 shadow-xl">
        <h2 className="text-lg font-bold text-action-primary mb-5 text-center">{title}</h2>

        <div className="flex flex-col gap-3">
          <DetailRow label="Category" value={getCategoryLabel(category)} />
          {subcategory && <DetailRow label="Type" value={getSubcategoryLabel(category, subcategory)} />}
          {position && <DetailRow label="Position" value={getPositionLabel(position as PositionId)} />}
          {apparatus && <DetailRow label="Apparatus" value={apparatus} />}
          {shiftTimeLabel && <DetailRow label="Shift" value={shiftTimeLabel} />}
        </div>

        <div className="flex gap-3 mt-6">
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            className="flex-1 py-3.5 text-base font-semibold rounded-lg border border-edge-default
                       bg-transparent text-content-secondary cursor-pointer
                       hover:bg-surface-sunken transition-colors duration-150
                       disabled:cursor-not-allowed disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={submitting}
            className="flex-1 py-3.5 text-base font-bold rounded-lg border-none
                       bg-success-600 text-white cursor-pointer
                       hover:bg-success-500 transition-colors duration-150
                       disabled:cursor-not-allowed disabled:opacity-60"
          >
            {submitting ? btnLoadingText : btnText}
          </button>
        </div>
      </div>
    </div>
  );
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between items-center px-3.5 py-2.5 bg-surface-sunken rounded-lg">
      <span className="text-xs text-content-tertiary uppercase tracking-wider">{label}</span>
      <span className="text-sm font-semibold text-content-primary">{value}</span>
    </div>
  );
}
